import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCode, FaRocket, FaLightbulb, FaMobile, FaCloud, FaPalette, FaDatabase, FaCog } from 'react-icons/fa';
import './Hero.css';

const Hero = () => {
  const floatingElements = [
    { Icon: FaCode, delay: 0, position: 'float-1', duration: 3 },
    { Icon: FaRocket, delay: 0.2, position: 'float-2', duration: 3.5 },
    { Icon: FaLightbulb, delay: 0.4, position: 'float-3', duration: 4 },
    { Icon: FaMobile, delay: 0.6, position: 'float-4', duration: 3.2 },
    { Icon: FaCloud, delay: 0.8, position: 'float-5', duration: 3.8 },
    { Icon: FaPalette, delay: 1, position: 'float-6', duration: 3.3 },
    { Icon: FaDatabase, delay: 1.2, position: 'float-7', duration: 4.2 },
    { Icon: FaCog, delay: 1.4, position: 'float-8', duration: 3.6 }
  ];

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 3,
    duration: 3 + Math.random() * 2
  }));

  return (
    <section className="hero" id="home">
      <div className="hero-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Floating Elements Around Content */}
      <div className="floating-elements-container">
        {floatingElements.map(({ Icon, delay, position, duration }, index) => (
          <motion.div
            key={index}
            className={`floating-element ${position}`}
            initial={{ opacity: 0, scale: 0, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: delay, ease: 'easeOut' }}
          >
            <motion.div
              className="element-inner"
              animate={{
                y: [0, -25, 0],
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{
                duration: duration,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: delay
              }}
            >
              <Icon />
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Floating Particles */}
      <div className="particles-container">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="particle"
            style={{
              top: particle.top,
              left: particle.left
            }}
            initial={{ opacity: 0, y: 0 }}
            animate={{
              opacity: [0, 0.6, 0],
              y: [0, -100, -200]
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: 'linear'
            }}
          />
        ))}
      </div>

      {/* Centered Content */}
      <div className="hero-container">
        <div className="hero-content-centered">
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge-dot"></span>
            <span>Transforming Ideas into Reality</span>
          </motion.div>

          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Building Tomorrow's
            <br />
            <span className="gradient-text">Digital Solutions</span>
            <br />
            Today
          </motion.h1>

          <motion.p
            className="hero-description"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            We craft exceptional software solutions that drive innovation and accelerate
            your business growth. From concept to deployment, we're your trusted technology partner.
          </motion.p>

          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link to="/contact">
              <motion.button
                className="btn-primary"
                whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(99, 102, 241, 0.6)' }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Project
                <FaArrowRight className="btn-icon" />
              </motion.button>
            </Link>

            <Link to="/portfolio">
              <motion.button
                className="btn-secondary"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(99, 102, 241, 0.3)' }}
                whileTap={{ scale: 0.95 }}
              >
                View Our Work
              </motion.button>
            </Link>
          </motion.div>

          <motion.div
            className="hero-stats"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="stat-item">
              <h3>30+</h3>
              <p>Projects Completed</p>
            </div>
           
            <div className="stat-divider"></div>
            <div className="stat-item">
              <h3>4+</h3>
              <p>Years Experience</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
      >
        <motion.div
          className="scroll-mouse"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="scroll-wheel"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
